import { AnimatePresence, motion } from "framer-motion";
import { X, Pause, ArrowRight, Trash } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney, relativeMinutes } from "./utils";
import type { CashierLabels, CashierOrder } from "./types";

export interface CashierParkedDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  parkedOrders: CashierOrder[];
  onResume: (orderId: string) => void;
  onVoid?: (orderId: string) => void;
  labels: CashierLabels;
  locale?: "tr" | "en";
}

export function CashierParkedDrawer({
  isOpen, onClose, parkedOrders, onResume, onVoid, labels, locale = "tr",
}: CashierParkedDrawerProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
          />
          <motion.aside
            role="dialog"
            aria-label={labels.parked.title}
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[420px] bg-biz-bg biz-shadow-lg flex flex-col"
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-biz-border">
              <div className="flex items-center gap-2">
                <Pause className="h-5 w-5 text-biz-accent" weight="fill" />
                <h2 className="text-base font-bold text-biz-foreground">{labels.parked.title}</h2>
                <span className="font-mono-num text-xs px-1.5 py-0.5 rounded-full bg-biz-muted text-biz-foreground-muted tabular-nums">
                  {parkedOrders.length}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="h-10 w-10 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted biz-focus-ring transition-colors flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-auto biz-scrollbar p-3 space-y-2">
              {parkedOrders.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center text-biz-foreground-muted py-12">
                  <Pause className="h-10 w-10 mb-3 text-biz-foreground-subtle" />
                  <p className="text-sm">{labels.parked.empty}</p>
                </div>
              )}
              {parkedOrders.map((o) => (
                <div
                  key={o.id}
                  className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-3 hover:border-biz-primary/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-biz-foreground truncate">
                        {o.parkedLabel ?? o.customerName ?? labels.parked.unnamed}
                      </p>
                      <p className="text-xs text-biz-foreground-muted mt-0.5">
                        {o.lines.length} ·{" "}
                        <span className="font-mono-num tabular-nums">
                          {formatMoney(o.totalMinor, labels.currencyPrefix, locale)}
                        </span>{" "}
                        · {relativeMinutes(o.openedAt)}{locale === "tr" ? "dk" : "m"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    {onVoid && (
                      <button
                        type="button"
                        onClick={() => onVoid(o.id)}
                        className={cn(
                          "h-9 px-2.5 rounded-biz-btn text-xs font-semibold inline-flex items-center gap-1",
                          "border border-biz-border text-biz-foreground-muted hover:text-biz-danger hover:bg-biz-danger-subtle",
                          "transition-colors biz-focus-ring",
                        )}
                      >
                        <Trash className="h-3.5 w-3.5" />
                        {labels.parked.void}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onResume(o.id)}
                      className={cn(
                        "h-9 flex-1 rounded-biz-btn text-xs font-semibold inline-flex items-center justify-center gap-1.5",
                        "bg-biz-primary text-biz-primary-foreground hover:bg-biz-primary-hover",
                        "transition-colors biz-focus-ring",
                      )}
                    >
                      {labels.parked.resume}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
